import { getMomentTz } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Button,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	type BundleQualityCheckPayload,
	getSawRecentBundles,
	type SawQualityCheck,
	type SawRecentBundle,
	upsertBundleQualityCheck,
} from "../repositories/sawWriteRepository";
import { rangeWithFraction32, toFraction32 } from "@/utils/fraction";
import { CardPaperSx, CardTitle, StateChip } from "./primitives";
import { ReclassifyBundleModal } from "./ReclassifyBundleModal";
import { useQualityCheckRefresh } from "../hooks/useQualityCheckRefresh";
import {
	EMPTY_VALUES,
	isImplausible,
	isOutOfSpec,
	POSITIONS,
	type Position,
	toCheckFields,
	toNumber,
	unusualMessage,
	valuesFor,
} from "../utils/qualityCheckMeasurements";

const moment = getMomentTz();

// Bundle-face cells on a 6-track grid: thirds on the top row (TL | MC | TR),
// halves on the bottom (BL | BR).
const GRID_POS: Record<Position, { gridColumn: string; gridRow: string }> = {
	TL: { gridColumn: "1 / span 2", gridRow: "1" },
	MC: { gridColumn: "3 / span 2", gridRow: "1" },
	TR: { gridColumn: "5 / span 2", gridRow: "1" },
	BL: { gridColumn: "1 / span 3", gridRow: "2" },
	BR: { gridColumn: "4 / span 3", gridRow: "2" },
};

interface Props {
	assetId: number | null;
	// Exiting bundle from the overview — preselected as the one to check.
	bundleId?: number | null;
	// Check picked for correction on the shift table: prefills the form and
	// saving updates that check (values, or the bundle if they picked wrong).
	editing?: SawQualityCheck | null;
	onEditDone?: () => void;
	onError?: (e: Error) => void;
}

// The edited check's bundle may have left the recent-bundles window; rebuild
// enough of it from the check row to select and display it.
const bundleFromCheck = (c: SawQualityCheck): SawRecentBundle => ({
	BundleId: c.BundleId as number,
	AssetId: 0,
	StartTime: c.BundleStartTime ?? c.CheckTime,
	EndTime: c.BundleEndTime,
	BarCount: c.BundleBarCount,
	AvgLength: null,
	WorkOrderId: null,
	IsWorkOrderIdManual: false,
	IsReclassified: c.IsCutback === true,
	ReclassifiedToProductId: null,
	PartNumber: null,
	SectionName: null,
	GradeName: null,
	MinLength: c.MinLength,
	MaxLength: c.MaxLength,
	IsCutback: c.IsCutback === true,
	HasCheck: true,
	CheckTL: c.CheckTL,
	CheckTR: c.CheckTR,
	CheckMC: c.CheckMC,
	CheckBL: c.CheckBL,
	CheckBR: c.CheckBR,
});

const bundleName = (b: SawRecentBundle) => `${b.BarCount ?? "—"} pcs bundle`;

const bundleDetail = (b: SawRecentBundle) =>
	`${moment(b.StartTime).format("HH:mm DD/MM")} – ${
		b.EndTime ? moment(b.EndTime).format("HH:mm DD/MM") : "Now"
	}`;

export const QualityCheckForm = ({
	assetId,
	bundleId,
	editing = null,
	onEditDone,
	onError,
}: Props) => {
	const queryClient = useQueryClient();
	const [values, setValues] = useState<Record<Position, string>>(() => ({
		...EMPTY_VALUES,
	}));
	const [selected, setSelected] = useState<SawRecentBundle | null>(null);
	const [saving, setSaving] = useState(false);
	// Out-of-tolerance save → offer to reclassify that bundle (assisted, #208).
	const [outOfSpecBundle, setOutOfSpecBundle] =
		useState<SawRecentBundle | null>(null);
	const [reclassifyBundle, setReclassifyBundle] =
		useState<SawRecentBundle | null>(null);
	// Picking an already-checked bundle → confirm before editing its check.
	const [confirmChecked, setConfirmChecked] = useState<SawRecentBundle | null>(
		null,
	);
	// Implausible values (likely typos) → confirm before saving anyway.
	const [confirmUnusual, setConfirmUnusual] = useState<string | null>(null);

	const { data: bundles = [] } = useQuery<SawRecentBundle[], Error>({
		queryKey: ["sawRecentBundles", assetId],
		queryFn: () => getSawRecentBundles(assetId as number),
		enabled: assetId != null,
		refetchInterval: 30_000,
		onError,
	});

	const isEditing = editing != null;

	useEffect(() => {
		if (editing == null || editing.BundleId == null) return;
		const b = bundleFromCheck(editing);
		setSelected(b);
		setValues(valuesFor(b));
	}, [editing]);

	// Preselect the exiting bundle (else the latest) until the operator picks
	// one. Already-checked bundles are never auto-picked — editing an existing
	// check must go through the explicit confirmation.
	useEffect(() => {
		if (isEditing || selected != null || bundles.length === 0) return;
		const def = bundles.find((b) => b.BundleId === bundleId) ?? bundles[0];
		if (!def.HasCheck) setSelected(def);
	}, [bundles, bundleId, selected, isEditing]);

	// Keep the edited bundle selectable even when it fell out of the recent list.
	const options = useMemo(
		() =>
			selected != null && !bundles.some((b) => b.BundleId === selected.BundleId)
				? [selected, ...bundles]
				: bundles,
		[bundles, selected],
	);

	const bundleChip = (b: SawRecentBundle) =>
		b.EndTime == null ? (
			<StateChip label="In progress" color="#b58900" />
		) : b.BundleId === bundleId ? (
			<StateChip label="Current" color="#2e7d32" />
		) : null;

	const hasAnyValue = POSITIONS.some((p) => values[p.key].trim() !== "");
	const canSave = assetId != null && selected != null && hasAnyValue && !saving;

	const handleSave = () => {
		if (assetId == null || selected == null) return;
		const unusual = unusualMessage(selected, values);
		if (unusual) setConfirmUnusual(unusual);
		else void performSave();
	};

	const performSave = async () => {
		if (assetId == null || selected == null) return;
		setSaving(true);
		try {
			const payload: BundleQualityCheckPayload = {
				qualityCheckId: editing?.QualityCheckId ?? null,
				assetId,
				bundleId: selected.BundleId,
				...toCheckFields(values),
			};
			const anyOut = POSITIONS.some((p) =>
				isOutOfSpec(selected, values[p.key]),
			);
			await upsertBundleQualityCheck(payload);
			await queryClient.invalidateQueries({ queryKey: ["sawQualityChecks"] });
			await queryClient.invalidateQueries({ queryKey: ["sawRecentBundles"] });
			setValues({ ...EMPTY_VALUES });
			setSelected(null);
			onEditDone?.();
			if (anyOut) setOutOfSpecBundle(selected);
		} catch (e) {
			onError?.(e as Error);
		} finally {
			setSaving(false);
		}
	};

	const handleReclassified = useQualityCheckRefresh();

	return (
		<Paper elevation={1} sx={CardPaperSx}>
			<CardTitle
				title="Bundle Quality Check"
				badge={
					isEditing ? <StateChip label="Editing" color="#3D5E9E" /> : undefined
				}
				action={
					<Box sx={{ display: "flex", gap: 0.5 }}>
						{isEditing && (
							<Button
								size="small"
								variant="outlined"
								color="inherit"
								onClick={() => {
									setSelected(null);
									setValues({ ...EMPTY_VALUES });
									onEditDone?.();
								}}
							>
								Cancel
							</Button>
						)}
						<Button
							size="small"
							variant="contained"
							color="success"
							disabled={!canSave}
							onClick={handleSave}
						>
							{saving ? "Saving…" : "Save"}
						</Button>
					</Box>
				}
			/>

			<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.6 }}>
				<Autocomplete
					size="small"
					options={options}
					value={selected}
					onChange={(_, next) => {
						const ownBundle = isEditing && next?.BundleId === editing?.BundleId;
						if (next?.HasCheck && !ownBundle) {
							setConfirmChecked(next);
							return;
						}
						setSelected(next);
						// Editing: switching bundle carries the typed values along.
						if (!isEditing) setValues(valuesFor(next));
					}}
					getOptionLabel={(b) => `${bundleName(b)} · ${bundleDetail(b)}`}
					isOptionEqualToValue={(a, b) => a.BundleId === b.BundleId}
					disabled={assetId == null}
					renderOption={({ key, ...props }, b) => (
						<Box
							component="li"
							key={key}
							{...props}
							sx={{ display: "flex", alignItems: "center", gap: 1 }}
						>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography fontWeight={700} sx={{ fontSize: "0.85rem" }}>
									{bundleName(b)}
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block" }}
								>
									{bundleDetail(b)}
								</Typography>
							</Box>
							{b.IsCutback && <StateChip label="Cutback" color="#757575" />}
							{b.HasCheck && <StateChip label="Checked" color="#3D5E9E" />}
							{bundleChip(b)}
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Bundle"
							placeholder="Select the bundle to check"
						/>
					)}
				/>
				{selected?.MinLength != null && selected?.MaxLength != null && (
					<Typography variant="caption" sx={{ color: "#666", px: 0.5 }}>
						Tolerance: {selected.MinLength}" – {selected.MaxLength}" ·{" "}
						{toFraction32(selected.MinLength)} –{" "}
						{toFraction32(selected.MaxLength)}
					</Typography>
				)}

				{/* Bundle face, two flat rows: TL | MC | TR over BL | BR. */}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(6, 1fr)",
						gap: 0.6,
					}}
				>
					{POSITIONS.map((p) => {
						const out = isOutOfSpec(selected, values[p.key]);
						const odd = isImplausible(selected, values[p.key]);
						return (
							<Box key={p.key} sx={GRID_POS[p.key]}>
								<TextField
									size="small"
									fullWidth
									type="number"
									label={p.label}
									value={values[p.key]}
									disabled={assetId == null || selected == null}
									error={out}
									sx={
										odd && !out
											? {
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "#ed6c02",
													},
													"& .MuiInputLabel-root": { color: "#ed6c02" },
												}
											: undefined
									}
									onChange={(e) => {
										const v = e.target.value;
										setValues((prev) => ({ ...prev, [p.key]: v }));
									}}
									slotProps={{
										htmlInput: { step: 0.01, inputMode: "decimal" },
										input: {
											// Tape-measure reading of the typed value, inline so it
											// never shifts the layout.
											endAdornment: (() => {
												const v = toNumber(values[p.key]);
												return v != null ? (
													<InputAdornment position="end">
														<Typography
															variant="caption"
															sx={{
																color: "text.disabled",
																whiteSpace: "nowrap",
															}}
														>
															{toFraction32(v)}
														</Typography>
													</InputAdornment>
												) : null;
											})(),
										},
									}}
								/>
							</Box>
						);
					})}
				</Box>
			</Box>

			<ConfirmationDialogRaw
				open={confirmChecked != null}
				title="Bundle already checked"
				message={
					isEditing
						? "This bundle already has its own check. Moving this check here will leave two checks on that bundle. Continue?"
						: "This bundle already has a quality check. Saving will update its existing measurements. Continue?"
				}
				onClose={(value) => {
					const bundle = confirmChecked;
					setConfirmChecked(null);
					if (value === "OK") {
						setSelected(bundle);
						if (!isEditing) setValues(valuesFor(bundle));
					}
				}}
			/>

			<ConfirmationDialogRaw
				open={confirmUnusual != null}
				title="Unusual measurements"
				message={confirmUnusual ?? ""}
				onClose={(value) => {
					setConfirmUnusual(null);
					if (value === "OK") void performSave();
				}}
			/>

			<ConfirmationDialogRaw
				open={outOfSpecBundle != null}
				title="Measurements out of tolerance"
				message={`Some measurements are outside the product tolerance${
					outOfSpecBundle?.MinLength != null &&
					outOfSpecBundle?.MaxLength != null
						? ` (${rangeWithFraction32(outOfSpecBundle.MinLength, outOfSpecBundle.MaxLength)})`
						: ""
				}. Reclassify this bundle?`}
				onClose={(value) => {
					const bundle = outOfSpecBundle;
					setOutOfSpecBundle(null);
					if (value === "OK") setReclassifyBundle(bundle);
				}}
			/>

			<ReclassifyBundleModal
				open={reclassifyBundle != null}
				assetId={assetId}
				bundleId={reclassifyBundle?.BundleId ?? null}
				onClose={() => setReclassifyBundle(null)}
				onReclassified={handleReclassified}
			/>
		</Paper>
	);
};
